import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { IconDefinition } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";

import { useIsMobile } from "src/hooks/useIsMobile";
import { colors } from "src/styles";

interface NavbarItemProps {
  title: string;
  href: string;
  icon: IconDefinition;
  isVisible: boolean;
  onClick?: () => void;
  isActive: boolean;
}

export const NavbarItem: React.FC<NavbarItemProps> = ({ title, href, icon, isVisible, onClick, isActive }) => {
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    onClick?.();
    navigate(href);
  };

  return (
    <>
      <Container type="button" onClick={handleClick}>
        <ItemBox $isMobile={isMobile ? 1 : 0} $isVisible={isVisible ? 1 : 0} $isActive={isActive ? 1 : 0}>
          <Icon
            icon={icon as IconProp}
            $isMobile={isMobile ? 1 : 0}
            $isActive={isActive ? 1 : 0}
            $isVisible={isVisible ? 1 : 0}
          />
          <Title
            $isActive={isActive ? 1 : 0}
            $isVisible={isVisible ? 1 : 0}
            style={{
              display: isVisible ? "block" : "none",
            }}
            $isMobile={isMobile ? 1 : 0}
          >
            {title}
          </Title>
        </ItemBox>
      </Container>
    </>
  );
};

export interface ItemBoxProps {
  $isVisible: number;
  $isMobile: number;
  $isActive: number;
}

const Container = styled.button`
  text-decoration: none;
  color: inherit;
  background: transparent;
  border: 0;
  padding: 0;
  width: 100%;
  text-align: left;
  cursor: pointer;
`;

const ItemBox = styled.div<ItemBoxProps>`
  display: flex;
  justify-content: ${({ $isVisible }) => ($isVisible ? "flex-start" : "center")};
  align-items: center;
  gap: ${({ $isVisible }) => ($isVisible ? "10px" : "0")};
  min-height: 42px;
  padding: 10px 12px;
  margin-bottom: 2px;
  background-color: ${({ $isActive }) => ($isActive ? colors.grey.light7 : "transparent")};
  border-radius: 10px;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: ${({ $isActive }) => ($isActive ? colors.grey.light7 : colors.grey.light14)};
  }

  ${({ $isMobile }) =>
    $isMobile &&
    css`
      border-bottom: 0;
      height: auto;
      display: flex;
      justify-content: flex-start;
    `};
`;

const Title = styled.label<ItemBoxProps>`
  color: ${({ $isActive }) => ($isActive ? colors.black.dark1 : colors.grey.dark3)};
  font-weight: ${({ $isActive }) => ($isActive ? "600" : "400")};
  font-size: 12px;
  letter-spacing: 0.01em;
  cursor: pointer;
  line-height: 1.3;
  display: ${({ $isVisible }) => ($isVisible ? "block" : "none")};

  ${({ $isMobile }) =>
    $isMobile &&
    css`
      display: block;
    `}
`;

const Icon = styled(FontAwesomeIcon)<ItemBoxProps>`
  color: ${({ $isActive }) => ($isActive ? colors.black.dark1 : colors.grey.dark6)};
  height: 16px;
  width: 16px;
  flex: 0 0 16px;

  ${({ $isMobile }: { $isMobile: number }) =>
    $isMobile &&
    css`
      display: block;
    `};
`;
